import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authService } from '../services/auth/AuthService';
import { UserIdManager } from '../utils/userIdManager';
import { URLs } from '../store/urls';

interface AuthContextType {
  isInitialized: boolean;
  isAuthenticated: boolean;
  userName: string;
  email: string;
  userId: string;
  login: (locale?: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (locale?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to sync user with backend after Keycloak auth
async function syncUserWithBackend(userId: string, email: string, userName: string) {
  try {
    console.log('[AuthContext] Syncing user with backend:', { userId, email, userName });
    
    const response = await fetch('/auth/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: userId,
        email: email,
        userName: userName
      })
    });

    if (response.ok) {
      const data = await response.json();
      console.log('[AuthContext] User synced successfully:', data);
      return true;
    } else {
      console.error('[AuthContext] Failed to sync user:', response.status, response.statusText);
      const errorText = await response.text();
      console.error('[AuthContext] Error details:', errorText);
      return false;
    }
  } catch (error) {
    console.error('[AuthContext] Error syncing user:', error);
    return false;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState('');

  useEffect(() => {
    const initAuth = async () => {
      console.log('[AuthContext] Initializing Keycloak...');
      
      // Check cached auth state first to avoid loader
      const cachedAuthState = sessionStorage.getItem('keycloak_auth_state');
      if (cachedAuthState) {
        try {
          const cached = JSON.parse(cachedAuthState);
          console.log('[AuthContext] Using cached auth state:', cached);
          setIsInitialized(true);
          setIsAuthenticated(cached.isAuthenticated);
          setUserName(cached.userName);
          setEmail(cached.email);
          setUserId(cached.userId);
        } catch (e) {
          console.warn('[AuthContext] Failed to parse cached auth state');
        }
      }
      
      await authService.init();
      
      setIsInitialized(authService.isInitialized);
      setIsAuthenticated(authService.isAuthenticated);
      setUserName(authService.userName);
      setEmail(authService.email);
      setUserId(authService.userId);
      
      // Cache auth state for faster reload
      sessionStorage.setItem('keycloak_auth_state', JSON.stringify({
        isAuthenticated: authService.isAuthenticated,
        userName: authService.userName,
        email: authService.email,
        userId: authService.userId
      }));
      
      // If authenticated, save userId from Keycloak to localStorage and sync with backend
      if (authService.isAuthenticated && authService.userId) {
        UserIdManager.setUserId(authService.userId);
        console.log('[AuthContext] UserId saved from Keycloak:', authService.userId);
        
        // Sync user with backend (non-blocking)
        syncUserWithBackend(
          authService.userId,
          authService.email,
          authService.userName
        ).catch(err => console.error('[AuthContext] Failed to sync user:', err));
      }
      
      console.log('[AuthContext] Keycloak initialized:', {
        isAuthenticated: authService.isAuthenticated,
        userName: authService.userName,
        userId: authService.userId
      });
    };
    
    initAuth();
  }, []);

  const login = useCallback(async (locale?: string) => {
    await authService.login(locale);
  }, []);

  const logout = useCallback(async () => {
    // Clear cached auth state on logout
    sessionStorage.removeItem('keycloak_auth_state');
    await authService.logout();
  }, []);

  const register = useCallback(async (locale?: string) => {
    await authService.register(locale);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isInitialized,
        isAuthenticated,
        userName,
        email,
        userId,
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to access AuthContext
 * @throws Error if used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

